import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  ViewChild
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { TranslocoService } from '@ngneat/transloco';
import { ConfirmModalService } from '../confirm-modal/confirm-modal.service';
import { upsertImpressionsSection } from '../impressions/impressions-sections.util';
import {
  FalseOcularSurfaceDiseaseCondition,
  InterpretationConditions,
  InterpretationView
} from './interpretation.model';
import { InterpretationService } from './interpretation.service';

interface InterpretationData {
  patientData: any;
  impressionsControl: FormControl;
  closeAfterProcess?: boolean;
}

@Component({
  selector: 'csi-interpretation',
  templateUrl: './interpretation.component.html',
  styleUrls: ['./interpretation.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InterpretationComponent {
  @ViewChild('interpretationEl', { static: false }) interpretationEl: ElementRef;
  public trueOcularSurfaceDiseaseEvidence: string[];
  public falseOcularSurfaceDiseaseEvidence: string[];
  public falseOcularSurfaceDiseaseCondition: FalseOcularSurfaceDiseaseCondition;
  public interpretationView: InterpretationView = 'text';
  public hasDryEyeMimickers = false;
  public isCopied: boolean;
  public modalView = true;
  public lang: string = this.translocoService.getActiveLang();
  public translateObject = this.translocoService.getTranslation(
    this.translocoService.getActiveLang()
  );

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: InterpretationData,
    public translocoService: TranslocoService,
    private interpretationService: InterpretationService,
    private confirmModalService: ConfirmModalService,
    private changeDetectorRef: ChangeDetectorRef,
    private dialogRef: MatDialogRef<InterpretationComponent>
  ) {
    this.isOcularSurfaceDisease();
  }

  isOcularSurfaceDisease() {
    let falseOSDSignsWithoutSymptoms: string[];
    let falseOSDSymptomsWithoutSigns: string[];
    this.trueOcularSurfaceDiseaseEvidence = this.interpretationService.getEvidenceForConditionsThatMatch(
      this.data.patientData,
      InterpretationConditions.trueOcularSurfaceDisease
    );
    falseOSDSignsWithoutSymptoms = this.interpretationService.getEvidenceForConditionsThatMatch(
      this.data.patientData,
      InterpretationConditions.falseOcularSurfaceDisease.signsWithoutSymptoms
    );
    falseOSDSymptomsWithoutSigns = this.interpretationService.getEvidenceForConditionsThatMatch(
      this.data.patientData,
      InterpretationConditions.falseOcularSurfaceDisease.symptomsWithoutSigns
    );

    if (falseOSDSymptomsWithoutSigns.length > 0) {
      this.falseOcularSurfaceDiseaseCondition = 'Symptoms without Signs';
      this.falseOcularSurfaceDiseaseEvidence = falseOSDSymptomsWithoutSigns;
    } else if (falseOSDSignsWithoutSymptoms.length > 0) {
      this.falseOcularSurfaceDiseaseCondition = 'Signs without Symptoms';
      this.falseOcularSurfaceDiseaseEvidence = falseOSDSignsWithoutSymptoms;
    }
  }

  public onInterpretationViewChange(newInterpretationView: InterpretationView) {
    this.interpretationView = newInterpretationView;
  }

  public setHasDryEyeMimickers(hasDryEyeMimickers) {
    this.hasDryEyeMimickers = hasDryEyeMimickers;
    setTimeout(() => this.changeDetectorRef.markForCheck());
  }

  public copyToImpressions() {
    this.confirmModalService
      .show(
        'impression.confirm.overwriteInterpretationTitle',
        'impression.confirm.overwriteInterpretationBody',
        'impression.confirm.overwriteSectionButton',
        'Cancel'
      )
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.modalView = false;
        this.changeDetectorRef.detectChanges();
        const currentImpressions = (this.data.impressionsControl.value || '').toString();
        this.data.impressionsControl.setValue(
          upsertImpressionsSection(
            currentImpressions,
            'interpretation',
            this.interpretationEl.nativeElement.innerHTML
          )
        );
        this.isCopied = true;
        this.modalView = true;
        this.changeDetectorRef.detectChanges();
        this.dialogRef.close();
      });
  }

  ngAfterViewInit() {
    if (this.data.closeAfterProcess) {
      setTimeout(() => {
        const processedHtml = this.interpretationEl.nativeElement.innerHTML;
        const noEvidence = this.translocoService.translate(
          'INTERPRETATION.No evidence of Dry Eye Disease.'
        );
        if (processedHtml.includes(noEvidence)) {
          this.dialogRef.close('');
        } else {
          const interpretationTitle = this.translocoService.translate(
            'INTERPRETATION.Interpretation'
          );
          const aiDisclaimer = this.translocoService.translate(
            'INTERPRETATION.interpretationAiDisclaimer'
          );
          this.dialogRef.close(`
            <h4><span style="text-decoration: underline;">${interpretationTitle}</span > (${aiDisclaimer})</h4>
            ${processedHtml}
          `);
        }
      });
    }
  }

  public changeLanguage(lang = 'en') {
    this.translocoService.load(lang).subscribe(() => {
      this.translocoService.setActiveLang(lang);
      this.translateObject = this.translocoService.getTranslation(
        this.translocoService.getActiveLang()
      );
    });
  }
}
